import React, { useState } from 'react';
import SignatureCanvas from './SignatureCanvas';

export interface MembershipAgreementData {
  signerName: string;
  signatureDataUrl: string | null;
  signedAt: string | null;
  agreementVersion: string;
}

interface MembershipAgreementFormProps {
  memberName?: string;
  monthlyFee?: number;
  onSubmit: (data: MembershipAgreementData) => Promise<void>;
  onCancel?: () => void;
  isSubmitting?: boolean;
}

/**
 * MembershipAgreementForm Component
 *
 * Displays the full membership agreement text and collects signature
 * before user can proceed to payment.
 */
const MembershipAgreementForm: React.FC<MembershipAgreementFormProps> = ({
  memberName = '',
  monthlyFee = 209,
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  const [signerName, setSignerName] = useState(memberName);
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Handle scroll to track if user has read the agreement
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const isAtBottom = Math.abs(element.scrollHeight - element.scrollTop - element.clientHeight) < 50;
    if (isAtBottom) {
      setHasScrolledToBottom(true);
    }
  };

  // Validate and submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Record<string, string> = {};

    if (!signerName.trim()) {
      newErrors.signerName = 'Please enter your name';
    }

    if (!signatureDataUrl) {
      newErrors.signature = 'Signature is required';
    }

    if (!hasScrolledToBottom) {
      newErrors.scroll = 'Please read the entire agreement before signing';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    await onSubmit({
      signerName: signerName.trim(),
      signatureDataUrl,
      signedAt: new Date().toISOString(),
      agreementVersion: '2026-01',
    });
  };

  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-[#3A5E63] text-white p-6 text-center">
          <h2 className="text-2xl font-serif">Membership Agreement</h2>
          <p className="text-[#B2D8C5] mt-1">会员协议</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Agreement Header */}
          <div className="mb-6 text-center border-b border-gray-200 pb-4">
            <h3 className="text-lg font-semibold text-[#232347]">
              Healing Lotus: Sound Therapy And Skin SPA
            </h3>
            <p className="text-gray-600 mt-2">
              This membership agreement between Healing Lotus: Sound Therapy And Skin SPA and
            </p>
            <p className="font-medium text-[#3A5E63] mt-1">{memberName || '[Member Name]'}</p>
            <p className="text-gray-600 mt-2">Shall be effective on the date of</p>
            <p className="font-medium text-[#3A5E63]">{today}</p>
            <p className="text-gray-600 mt-2">
              The fee for membership is <span className="font-semibold">${monthlyFee}</span> a month
            </p>
          </div>

          {/* Scroll indicator */}
          {!hasScrolledToBottom && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4 text-sm text-yellow-800">
              Please scroll through and read the entire agreement before signing.
            </div>
          )}

          {/* Agreement Content - Scrollable */}
          <div
            className="bg-gray-50 rounded-lg p-4 mb-6 max-h-96 overflow-y-auto text-sm text-gray-700 space-y-4"
            onScroll={handleScroll}
          >
            {/* Terms and Conditions */}
            <h4 className="font-semibold text-[#232347]">Membership Terms and Conditions:</h4>

            <p>
              1. By agreeing to these terms and conditions, you, as Member, are confirming that you are at
              least twenty-one (21) years of age and agree to abide by and accept the terms and conditions
              contained herein. The terms and conditions apply to your Membership with Healing Lotus: Sound
              Therapy And Skin SPA inclusive of all affiliates, (collectively, the "Company").
            </p>

            <p>
              2. Membership does not include: discounts for any party other than Member; access to
              facilities after hours; special appointment setting or scheduling; guarantees of appointment
              times and/or dates; guarantee that particular clinicians will be available to administer
              services; guarantee of specific results from any treatments; or any warranties for services.
            </p>

            <p>
              3. The benefits of Membership will inure to the Member only during the active term of
              Membership. Company has the right to change these terms and conditions at any time for any
              reason at its discretion, including services and discounts offered. Notice of change of terms
              will be provided to each Member within sixty (60) days of any changes made.
            </p>

            <p>
              4. Membership requires a minimum commitment of two (2) months (60 days) from the subscription
              start date. After the minimum commitment period, Member may cancel the membership at any time.
              Cancellation will take effect at the end of the current billing period, and Member will
              continue to receive benefits until that date.
            </p>

            <p>
              5. This Agreement is personal to the member(s) and may not be assigned, transferred or
              otherwise disposed of by the member(s).
            </p>

            <hr className="my-4" />

            {/* Automatic Payment Agreement */}
            <h4 className="font-semibold text-[#232347]">Automatic Payment Agreement:</h4>

            <p>
              1. Monthly membership payments shall be made in advance by direct debit from the member's
              designated credit/debit account. This information will be kept on file by Healing Lotus:
              Sound Therapy And Skin SPA.
            </p>

            <p>
              2. You will be billed on the same day of the month each month according to the date that you
              initiated the contract and membership. We cannot change the billing date.
            </p>

            <p>
              3. You are responsible to ensure that the credit card number on file with Healing Lotus:
              Sound Therapy And Skin SPA is valid so that the monthly fee can be charged to that card. If
              there is not a valid credit card on file at the time the Monthly Fee is charged and one is
              not provided within ten days after the regularly scheduled date for payment, Healing Lotus:
              Sound Therapy And Skin SPA may exercise any and all rights available including suspension of
              your membership and/or assessing a late payment fee of $35.00.
            </p>

            <p>
              4. Healing Lotus: Sound Therapy And Skin SPA reserves the right to review subscriptions
              periodically. Members will be given at least a 60 days' notice in writing of any changes.
            </p>

            <p>
              5. All payments due hereunder will be collected by Electronic Funds Transfer ("EFT") and
              charged to the valid debit or credit card provided by you.
            </p>

            <p>
              6. If EFT payment is declined or not honored for any monthly Membership charge, then the
              Company reserves the right to accelerate the unpaid Membership dues and demand payment in
              full for all pending monthly charges remaining in the Membership term.
            </p>

            <hr className="my-4" />

            {/* Termination or Suspension */}
            <h4 className="font-semibold text-[#232347]">Termination or Suspension of Membership:</h4>

            <p>
              1. Please be advised that this Agreement is noncancellable. The only exceptions to this
              policy are: (i) your being called to active duty; (ii) if the membership and/or the primary
              Spa locations fails to open or permanently discontinues operations.
            </p>

            <p>
              2. A 30 day written notice is required to cancel your membership. If notice is not provided,
              then the Membership term will renew automatically for an additional month until proper notice
              of cancellation is provided.
            </p>

            <p>
              3. Company may cancel any Membership at any time for any reason without providing any refund
              for paid Membership fees. Violations that may result in automatic termination include:
              providing false information, invalid payment details, failure to update payment information,
              serious breach of rules, or unpaid dues for 30 days.
            </p>

            <p>
              4. Membership may be suspended (but not cancelled) for up to 90 days with a medical
              certificate. An administration charge of $25 per month applies during suspension.
            </p>

            <hr className="my-4" />

            {/* Rules */}
            <h4 className="font-semibold text-[#232347]">Rules:</h4>

            <p>1. Gift Cards may be redeemed to pay any portion of the membership fee.</p>
            <p>2. The membership fee cannot be combined with any other promotion and/or discount.</p>
            <p>
              3. Healing Lotus reserves the right to vary, add or eliminate any services and facilities.
            </p>
            <p>4. Healing Lotus reserves the right to close or modify facility hours.</p>

            <hr className="my-4" />

            {/* Waiver */}
            <h4 className="font-semibold text-[#232347]">Waiver & Liability Release:</h4>

            <p>
              1. The Company is not responsible nor liable for any injury or loss of property that occurs
              during administration of treatments or at any time you are on Company premises, whether
              resulting from negligence of the Company or its agents, employees, independent contractors,
              representatives, other Members or clients, other third-party affiliates, or any other person
              or entity.
            </p>

            <p>
              2. In consideration of this Membership and access to the discounts and services provided, you
              hereby waive, covenant not to sue and release any and all claims which may be brought by you
              or your family, estate, heirs and/or assigns, against the Company, its owners, employees,
              representatives, independent contractors and agents from any and all present or future claims
              of loss, theft, injury or damages of any kind.
            </p>

            <hr className="my-4" />

            {/* Representations */}
            <h4 className="font-semibold text-[#232347]">Representations and Warranties:</h4>

            <p>
              By signing this Agreement, you confirm that you are at least twenty-one (21) years of age,
              have access to an electronic copy of these terms and conditions, agree to all terms and
              conditions contained herein, and acknowledge the Company has not provided any express or
              implied warranty or representation other than those contained herein. You also confirm that
              you are in good physical condition and are physically capable of receiving cosmetic medical
              services from the Company.
            </p>

            <hr className="my-4" />

            {/* Governing Law */}
            <h4 className="font-semibold text-[#232347]">Governing Law; Severability; Venue:</h4>

            <p>
              These terms and conditions and all related documents are governed by, and construed in
              accordance with, the laws of the State of Texas. Any action by either party relating to this
              agreement must be brought in Tarrant County, Texas.
            </p>

            <hr className="my-4" />

            {/* Entire Agreement */}
            <h4 className="font-semibold text-[#232347]">Entire Agreement:</h4>

            <p>
              This Agreement constitutes the sole and entire agreement of the Member and Company with
              respect to the subject matter of this Agreement, and supersedes all prior and contemporaneous
              understandings, agreements, representations, and warranties, both written and oral.
            </p>

            <div className="mt-6 p-4 bg-[#F1F7F4] rounded-lg text-center">
              <p className="font-medium text-[#3A5E63]">
                By accepting these terms and conditions, you assert that you are of sound mind, understand
                all provisions, and will abide by all terms and conditions herein contained.
              </p>
            </div>

            {/* End marker for scroll detection */}
            <div className="h-4" />
          </div>

          {errors.scroll && (
            <p className="text-red-500 text-sm mb-4">{errors.scroll}</p>
          )}

          {/* Signer Name */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your Full Name <span className="text-gray-400">您的全名</span> *
            </label>
            <input
              type="text"
              value={signerName}
              onChange={(e) => {
                setSignerName(e.target.value);
                if (errors.signerName) {
                  setErrors(prev => ({ ...prev, signerName: '' }));
                }
              }}
              placeholder="Enter your full legal name"
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3A5E63] ${
                errors.signerName ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.signerName && (
              <p className="text-red-500 text-xs mt-1">{errors.signerName}</p>
            )}
          </div>

          {/* Signature */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Signature <span className="text-gray-400">签字</span> *
            </label>
            <SignatureCanvas
              onSignatureChange={(dataUrl) => {
                setSignatureDataUrl(dataUrl);
                if (errors.signature) {
                  setErrors(prev => ({ ...prev, signature: '' }));
                }
              }}
            />
            {errors.signature && (
              <p className="text-red-500 text-xs mt-1">{errors.signature}</p>
            )}
          </div>

          {/* Date */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date <span className="text-gray-400">日期</span>
            </label>
            <p className="text-gray-700">{today}</p>
          </div>

          {/* Buttons */}
          <div className="flex gap-4 pt-4 border-t border-gray-200">
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                disabled={isSubmitting}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={isSubmitting || !hasScrolledToBottom}
              className={`flex-1 px-6 py-3 font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 ${
                hasScrolledToBottom
                  ? 'bg-[#3A5E63] text-white hover:bg-[#2d4a4d]'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              } disabled:opacity-50`}
            >
              {isSubmitting ? (
                <>
                  <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
                  Processing...
                </>
              ) : (
                'I Agree & Continue to Payment'
              )}
            </button>
          </div>

          {!hasScrolledToBottom && (
            <p className="text-center text-sm text-gray-500 mt-3">
              Scroll to the bottom of the agreement to enable signing
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default MembershipAgreementForm;
